import { useEffect, useState } from 'react'
import { parishesApi, type ParishResponse } from '@/api/endpoints/parishes'
import { useAuth } from '@/stores/auth'

interface Props {
  canCreate: boolean
  onCreateNew: () => void
  onSwitched?: (parishId: string) => void
  refreshTrigger?: number // When this changes, reload the list
}

export default function ParishSelector({ canCreate, onCreateNew, onSwitched, refreshTrigger }: Props) {
  const [items, setItems] = useState<ParishResponse[]>([])
  // Controlled by the active parish, so the header always shows where you are working
  const activeId = useAuth((st) => st.user?.parish_id) ?? ''
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        const list = await parishesApi.list()
        setItems(list)
      } catch (e) {
        setError('Failed to load parishes')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [refreshTrigger])

  const onChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    if (value === '__create__') {
      onCreateNew()
      return
    }
    if (value) {
      try {
        await parishesApi.switch(value)
        onSwitched?.(value)
      } catch {
        setError('Failed to switch parish')
      }
    }
  }

  return (
    <div className="flex items-center gap-2">
      <label className="text-sm text-gray-600">Parish:</label>
      <select className="input-field text-sm py-1" disabled={loading} onChange={onChange} value={activeId}
        title="Switch which parish you are working in, or create a new one">
        {!activeId && <option value="" disabled>{loading ? 'Loading…' : 'Select parish'}</option>}
        {items.map(p => (
          <option key={p.id} value={p.id}>{p.name}</option>
        ))}
        {canCreate && <option value="__create__">+ Create New Parish…</option>}
      </select>
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  )
}



